import { Input, Select } from "antd";
import { Option } from "../../models/generics";
import { Dayjs } from "dayjs";
import { Facility, TimeSlot } from "../../types/Structure";
import { useCallback, useEffect, useState } from "react";
import { BookingScope, BookingType } from "../../enums/BookingScope";
import { MatchType } from "../../types/Result";
import { Structures } from "../../api/Structure";
import { useToasts } from "../../contexts/toasts";
import { TailSpin } from "react-loader-spinner";
import TextArea from "antd/es/input/TextArea";
import BookingColorPicker from "./BookingColorPicker";
import BookingTypeSelect from "./BookingTypeSelect";
import "./Booking.css";

type Props = {
  sport: Option;
  date?: Dayjs;
  time?: TimeSlot;
  facility?: Facility;
  bookingData: {
    matchType?: MatchType;
    bookingType?: BookingType;
    scope: BookingScope;
    inviteLevel?: number;
    coach?: string;
    player1Team1?: string;
    player2Team1?: string;
    player1Team2?: string;
    player2Team2?: string;
    color?: string;
    customType?: string;
  };
  handleFieldChange: (key: string) => (value: string) => void;
  handleFieldClear: (key: string) => () => void;
  handleInviteLevelChange: (e: number) => void;
};

const Spinner = () => (
  <div className="d-flex justify-content-center py-2">
    <TailSpin height={20} width={20} color="#f25d0b" radius="1" />
  </div>
);

export default function NewBookingModal({
  sport,
  date,
  time,
  facility,
  handleFieldChange,
  handleInviteLevelChange,
  handleFieldClear,
  bookingData,
}: Props) {
  const { showToast } = useToasts();
  const [usersList, setUsersList] = useState<Option[]>([]);
  const [coachesList, setCoachesList] = useState<Option[]>();

  const initFavorites = useCallback(() => {
    Structures.getHostFavorites().then(
      ({ data }) => {
        setUsersList(
          data.list.map((user) => ({
            label: `${user.user.name} ${user.user.familyName}`,
            value: user.userId,
          }))
        );
      },
      () => {
        showToast("players-error", "Impossibile ottenere la lista dei giocatori", "", "danger");
        setUsersList([]);
      }
    );
  }, [showToast]);

  const initCoaches = useCallback(() => {
    Structures.getStructureCoaches().then(
      ({ data }) => {
        setCoachesList(
          data.list.map((coach) => ({
            label: `${coach.user.name} ${coach.user.familyName}`,
            value: coach.userId,
          }))
        );
      },
      () => {
        showToast("coaches-error", "Impossibile ottenere la lista dei maestri", "", "danger");
        setCoachesList([]);
      }
    );
  }, [showToast]);

  useEffect(() => {
    if ([1, 2, 7].includes(sport.value)) {
      initFavorites();
      initCoaches();
    }
  }, [initFavorites, initCoaches, sport]);

  const handleNoteChange = useCallback(
    () => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      handleFieldChange("notes")(e.target.value);
    },
    [handleFieldChange]
  );

  const handleColorChange = useCallback(
    (color?: string) => {
      if (color) handleFieldChange("color")(color);
      else handleFieldClear("color")();
    },
    [handleFieldChange, handleFieldClear]
  );

  // Tipologia personalizzata (es. "Manutenzione"): non è una partita aperta,
  // quindi la prenotazione resta privata.
  const handleBookingTypeChange = useCallback(
    (bookingType: BookingType, customType?: string) => {
      handleFieldChange("bookingType")(bookingType);
      if (customType === undefined) {
        handleFieldClear("customType")();
      } else {
        handleFieldChange("customType")(customType);
        if (bookingData.scope !== BookingScope.Private)
          handleFieldChange("scope")(BookingScope.Private);
      }
    },
    [handleFieldChange, handleFieldClear, bookingData.scope]
  );

  const isCustomType = bookingData.customType !== undefined;

  const isDouble =
    sport.value === 1 || bookingData.matchType === MatchType.Double;

  const hasSportPlayers = ["Padel", "Tennis", "Pickleball"].includes(sport.label as string);
  const hasTennisPickleball = ["Tennis", "Pickleball"].includes(sport.label as string);

  return (
    <div className="bm-container">

      {/* ── Sezione: riepilogo ── */}
      <div className="bm-section">
        <div className="bm-section-title">Riepilogo</div>

        <div className="bm-info-row">
          <span className="bm-label">Sport</span>
          <span className="bm-value">{sport.label}</span>
        </div>

        <div className="bm-info-row">
          <span className="bm-label">Data e ora</span>
          <span className="bm-value">
            {date ? new Date(date.toISOString()).toLocaleDateString("it-IT") : "—"} — {time?.time ?? "—"}
          </span>
        </div>

        <div className="bm-info-row">
          <span className="bm-label">Campo</span>
          <span className="bm-value">
            {facility?.name ?? "—"} · {facility?.type ?? ""}
          </span>
        </div>
      </div>

      {/* ── Sezione: impostazioni ── */}
      <div className="bm-section">
        <div className="bm-section-title">Impostazioni</div>

        {/* Formato singolare/doppio (solo Tennis e Pickleball) */}
        {hasTennisPickleball && (
          <div className="bm-field" style={{ borderTop: "none", paddingTop: 0 }}>
            <span className="bm-edit-label">Formato</span>
            <Select
              options={[
                { label: "Singolare", value: MatchType.Single },
                { label: "Doppio", value: MatchType.Double },
              ]}
              className="w-100 p-0"
              placeholder="Scegli il formato"
              value={bookingData.matchType}
              onSelect={handleFieldChange("matchType")}
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          </div>
        )}

        {/* Tipo prenotazione */}
        <div className={`bm-field ${hasTennisPickleball ? "" : "bm-field-first"}`}
          style={hasTennisPickleball ? {} : { borderTop: "none", paddingTop: 0 }}>
          <span className="bm-edit-label">Tipo prenotazione</span>
          <BookingTypeSelect
            bookingType={bookingData.bookingType}
            customType={bookingData.customType}
            onChange={handleBookingTypeChange}
          />
        </div>

        {/* Maestro (solo allenamento) */}
        {[1, 2, 7].includes(sport.value) && !isCustomType && bookingData.bookingType === BookingType.Training && (
          <div className="bm-field">
            <span className="bm-edit-label">Maestro</span>
            {coachesList ? (
              <Select
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                allowClear
                onClear={handleFieldClear("coach")}
                options={coachesList}
                className="w-100 p-0"
                placeholder="Scegli il maestro"
                value={bookingData.coach}
                onSelect={handleFieldChange("coach")}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            ) : <Spinner />}
          </div>
        )}

        {/* Visibilità (le tipologie personalizzate sono sempre private) */}
        {[1, 2, 7].includes(sport.value) && !isCustomType && (
          <div className="bm-field">
            <span className="bm-edit-label">Visibilità</span>
            <Select
              options={[
                { label: "Privata", value: BookingScope.Private },
                { label: "Pubblica", value: BookingScope.Open },
              ]}
              className="w-100 p-0"
              placeholder="Scegli la visibilità"
              value={bookingData.scope}
              onSelect={handleFieldChange("scope")}
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          </div>
        )}

        {/* Livello minimo (solo pubblica) */}
        {bookingData.scope === BookingScope.Open && (
          <div className="bm-field">
            <span className="bm-edit-label">Livello minimo partecipanti</span>
            <Input
              value={bookingData.inviteLevel}
              type="number"
              min={4.5}
              max={10.5}
              step={0.5}
              onChange={(e) => {
                let value = parseFloat(e.target.value);
                if (value < 4.5) value = 4.5;
                if (value > 10) value = 10;
                handleInviteLevelChange(value);
              }}
            />
          </div>
        )}

        {/* Colore del blocco nel calendario */}
        <div className="bm-field">
          <span className="bm-edit-label">Colore nel calendario</span>
          <BookingColorPicker
            value={bookingData.color}
            onChange={handleColorChange}
          />
        </div>
      </div>

      {/* ── Sezione: partecipanti ── */}
      {hasSportPlayers && (
        <div className="bm-section">
          <div className="bm-section-title">Partecipanti</div>

          <div className="d-flex flex-column gap-2">
            {/* Squadra 1 */}
            <div className="bm-field" style={{ borderTop: "none", paddingTop: 0 }}>
              <span className="bm-edit-label">
                {isDouble ? "Coppia" : "Giocatore"} 1
              </span>
              <div className="d-flex flex-column gap-2">
                <Select
                  showSearch
                  options={usersList.filter(
                    (user) =>
                      user.value !== bookingData.player2Team1 &&
                      user.value !== bookingData.player1Team2 &&
                      user.value !== bookingData.player2Team2
                  )}
                  className="w-100 p-0"
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                  placeholder="Giocatore 1"
                  value={bookingData.player1Team1}
                  onSelect={handleFieldChange("player1Team1")}
                  onClear={handleFieldClear("player1Team1")}
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
                {isDouble && (
                  <Select
                    showSearch
                    options={usersList.filter(
                      (user) =>
                        user.value !== bookingData.player1Team1 &&
                        user.value !== bookingData.player1Team2 &&
                        user.value !== bookingData.player2Team2
                    )}
                    className="w-100 p-0"
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    placeholder="Giocatore 2"
                    value={bookingData.player2Team1}
                    onSelect={handleFieldChange("player2Team1")}
                    allowClear
                    onClear={handleFieldClear("player2Team1")}
                    getPopupContainer={(trigger) => trigger.parentNode}
                  />
                )}
              </div>
            </div>

            {/* Squadra 2 */}
            <div className="bm-field">
              <span className="bm-edit-label">
                {isDouble ? "Coppia" : "Giocatore"} 2
              </span>
              <div className="d-flex flex-column gap-2">
                <Select
                  showSearch
                  options={usersList.filter(
                    (user) =>
                      user.value !== bookingData.player1Team1 &&
                      user.value !== bookingData.player2Team1 &&
                      user.value !== bookingData.player2Team2
                  )}
                  className="w-100 p-0"
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="Giocatore 1"
                  value={bookingData.player1Team2}
                  onSelect={handleFieldChange("player1Team2")}
                  allowClear
                  onClear={handleFieldClear("player1Team2")}
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
                {isDouble && (
                  <Select
                    showSearch
                    options={usersList.filter(
                      (user) =>
                        user.value !== bookingData.player1Team1 &&
                        user.value !== bookingData.player2Team1 &&
                        user.value !== bookingData.player1Team2
                    )}
                    className="w-100 p-0"
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    placeholder="Giocatore 2"
                    value={bookingData.player2Team2}
                    onSelect={handleFieldChange("player2Team2")}
                    allowClear
                    onClear={handleFieldClear("player2Team2")}
                    getPopupContainer={(trigger) => trigger.parentNode}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Sezione: note ── */}
      <div className="bm-section">
        <div className="bm-section-title">Note</div>
        <TextArea
          rows={3}
          onChange={handleNoteChange()}
          size="large"
          placeholder="Note..."
        />
      </div>

    </div>
  );
}
